import { BreadcrumbsWrapper } from '../Layout/BreadcrumbsWrapper';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import ProductPageLoadingState from './ProductPageLoadingState';
import { theme } from '../Theming/Theming';
import React, { useContext } from 'react';
import { styled } from 'linaria/react';
import RelatedProducts from './RelatedProducts/RelatedProducts';
import ProductDetails from './ProductDetails';
import ProductTabs from './ProductTabs/ProductTabs';
import Releware from './Releware';
import CustomData from './CustomData';
import { ContentPadding } from '../Layout/Content/Content';
import MaxWidth from '../Layout/MaxWidth';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import ProductRow from '../ui/ProductRow/ProductRow';
import ImageSlider from './ImageSlider/ImageSlider';
import AddonProducts from './AddonProducts/AddonProducts';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { KlaviyoTrackViewedItem } from '../Klaviyo';
import LipscoreWidget from '../Lipscore/LipscoreWidget';

const ProductPageContainer = styled(MaxWidth)`
  display: flex;
  align-items: center;
  flex-direction: column;
  ${ContentPadding}
`;

const ProductContainer = styled('div')`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  justify-content: space-between;
  ${theme.below.lg} {
  }
  .lipscore-reviews-wrapper {
    width: 100%;
    max-width: 800px;
    margin: 0 auto 4rem;
  }
`;

export const Section = styled('section')`
  width: 100%;
  padding-bottom: 0.5rem;
  ${theme.only.md} {
    padding-bottom: 1rem;
    &.product-images-wrapper {
      width: 45%;
    }
    &.product-info-wrapper {
      width: 50%;
    }
  }
  ${theme.above.lg} {
    padding-bottom: 2rem;
    &.product-images-wrapper {
      width: 48%;
    }
    &.product-info-wrapper {
      width: 48%;
    }
  }
  ${theme.below.sm} {
    &.product-images-wrapper {
      margin-left: -${theme.general.pagePadding.sm};
      width: 100vw;
      .main-slider {
        width: 100vw;
      }
    }
  }
`;

export const SectionHeading = styled('h2')`
  font-size: 1.2rem;
  margin-bottom: 1rem;
`;

const ProductTabsWrapper = styled('div')`
  margin-top: 1rem;
  width: 100%;
  ${theme.above.md} {
    //width: 50%;
  }
`;

const Product = ({ result: { data, loading }, product }) => {
  const initialVariant = useVariantFromUrl();

  //BUG FIX IN FLIGHT TO SHOW CORRECT IMAGE WHEN FIRST VARIANT IS SELECTED (TODO)
  //const [firstVariant, setFirstVariant] = useState(false);

  //GET VARIANTS HANDLING
  const variantHandler = useProductVariants(
    product && product.articleNumber ? product : undefined,
    { initialVariant, preselectCheapest: false }
  );

  //VARIANTS VARIABLES
  const { selectedVariant: selectedVariation } = variantHandler;

  const { selectedChannel } = useContext(ChannelContext);

  // RETURN EARLY IF CACHE
  if (!product)
    return (
      <ProductPageContainer>
        <ProductContainer>
          <ProductPageLoadingState />
        </ProductContainer>
      </ProductPageContainer>
    );

  //CUSTOM FIX TO SHOW VARIANT IMAGE WHEN JUST COLOR IS SELECTED
  const variantImageSlider =
    selectedVariation ||
    (variantHandler.missingOptions?.length === 1 &&
      variantHandler.getVariantForSelection(
        variantHandler.missingOptions[0].values[0],
        variantHandler.missingOptions[0]
      ));

  const parents = getParentOrCanonicalRoute(data?.route?.parents, product);
  const breadcrumbProps = {
    breadcrumbs: data?.route?.breadcrumbs,
    parents
  };

  //PARENT ID
  const path = data?.route?.path;
  let parentCategory = false;

  if (product.categories && product.categories.length > 0) {
    parentCategory = product.categories[0];

    if (path.search(theme.config.routes.blog.path) === -1) {
      const parentCartegories = product.categories.filter(
        cat => path.search(cat.primaryRoute.path) !== -1
      );
      parentCategory = parentCartegories[parentCartegories.length - 1];
    }
  }
  const parentId = parentCategory?.id;

  return (
    <ProductPageContainer>
      <KlaviyoTrackViewedItem
        product={product}
        productId={product.id}
        selectedChannel={selectedChannel}
      />
      {breadcrumbProps && <BreadcrumbsWrapper {...breadcrumbProps} />}
      <ProductContainer>
        <CustomData product={product}>
          {(customFields, customTabs, customData) => (
            <>
              <Section className="product-images-wrapper">
                <ImageSlider
                  product={product}
                  customData={customData}
                  selectedVariation={variantImageSlider}
                />
              </Section>
              <Section className="product-info-wrapper">
                <ProductDetails
                  {...{
                    product,
                    selectedVariation,
                    variantHandler,
                    breadcrumbProps,
                    customData
                  }}
                />
              </Section>
              <AddonProducts customData={customData} />
              <ProductTabsWrapper>
                <ProductTabs
                  product={product}
                  customFields={customFields}
                  customTabs={customTabs}
                  customData={customData}
                  loading={loading}
                  selectedVariation={selectedVariation}
                />
              </ProductTabsWrapper>
            </>
          )}
        </CustomData>

        <div className="lipscore-reviews-wrapper">
          <LipscoreWidget type="review-list" product={product} />
        </div>
        {parentId && (
          <ProductRow
            id={parentId}
            slider={true}
            title={'Others also bought'}
            subTitle={'May we suggest'}
            orderBy="BESTSELLER"
          />
        )}
        {product.relatedProducts && product.relatedProducts.length > 0 && (
          <RelatedProducts products={product.relatedProducts} />
        )}
        {product.recommendedProducts && (
          <>
            {product.recommendedProducts.shuffledToplist.length > 0 && (
              <Releware
                products={product.recommendedProducts.shuffledToplist}
                title={'Popular products'}
              />
            )}
            {product.recommendedProducts.viewed.length > 0 && (
              <Releware
                products={product.recommendedProducts.viewed}
                title={'Others also looked at'}
              />
            )}
            {product.recommendedProducts.bought.length > 0 && (
              <Releware
                products={product.recommendedProducts.bought}
                title={'Others also bought'}
              />
            )}
          </>
        )}
      </ProductContainer>
    </ProductPageContainer>
  );
};

export default Product;
